import {
  loadOpenPencilSourceFile,
  requestOpenPencilFigRevision,
  type OpenPencilFigRevisionResult,
} from "./openpencil-figma-client";
import type {
  OpenPencilFigEditPatch,
  OpenPencilFigRevisionEvidence,
  OpenPencilFigmaSourceBundle,
} from "./openpencil-figma-source";
import {
  OPENPENCIL_BROWSER_REVISION_PROOF_SCHEMA,
  type OpenPencilBrowserRevisionProof,
  type OpenPencilBrowserRevisionUnavailableReason,
  type OpenPencilBrowserRevisionWorkerRequest,
  type OpenPencilBrowserRevisionWorkerResponse,
} from "./openpencil-browser-revision-runtime";

interface BrowserRevisionWorker {
  onmessage:
    | ((event: MessageEvent<OpenPencilBrowserRevisionWorkerResponse>) => void)
    | null;
  onerror: ((event: ErrorEvent) => void) | null;
  postMessage(message: unknown, transfer: Transferable[]): void;
  terminate(): void;
}

export interface OpenPencilBrowserRevisionArtifact {
  blob: Blob;
  fileName: string;
  proof: OpenPencilBrowserRevisionProof;
}

/**
 * Raised when the browser cannot run the .fig engine AT ALL, as opposed to
 * failing on this source or this patch. Only this error degrades the flow.
 */
export class OpenPencilBrowserEngineUnavailableError extends Error {
  readonly reason: OpenPencilBrowserRevisionUnavailableReason;
  constructor(reason: OpenPencilBrowserRevisionUnavailableReason, message: string) {
    super(message);
    this.name = "OpenPencilBrowserEngineUnavailableError";
    this.reason = reason;
  }
}

/**
 * Whether the browser proof actually happened.
 *
 * REQUIRED on the result, and not merely implied by a null `browserArtifact`,
 * so a consumer cannot read "no artifact" as "nothing to show" and quietly
 * present a server-only revision as browser-proved. `verified` is the ONLY
 * value under which `assertCanonicalParity` ran.
 */
export type OpenPencilBrowserProofOutcome =
  | { status: "verified" }
  | {
      status: "unavailable";
      reason: OpenPencilBrowserRevisionUnavailableReason;
      detail: string;
    };

export interface OpenPencilBrowserFirstRevisionResult extends OpenPencilFigRevisionResult {
  /** `null` exactly when `browserProof.status` is `"unavailable"`. */
  browserArtifact: OpenPencilBrowserRevisionArtifact | null;
  browserProof: OpenPencilBrowserProofOutcome;
}

interface BrowserFirstRevisionDependencies {
  loadSourceFile: typeof loadOpenPencilSourceFile;
  runBrowserRevision: typeof runOpenPencilBrowserRevisionWorker;
  commitCanonicalRevision: typeof requestOpenPencilFigRevision;
}

function createBrowserRevisionWorker(): BrowserRevisionWorker {
  return new Worker(
    new URL("./openpencil-browser-revision.worker.ts", import.meta.url),
    { type: "module", name: "cogo-openpencil-fig-revision" },
  );
}

function browserRevisionFileName(bundle: OpenPencilFigmaSourceBundle): string {
  const stem = bundle.fileName.replace(/\.fig$/i, "");
  const nextRevision = (bundle.revisionLineage?.sourceRevision ?? 0) + 1;
  return `${stem}.browser-r${nextRevision}.fig`;
}

async function sha256Buffer(buffer: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return [...new Uint8Array(digest)]
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

function sameMetrics(
  left: OpenPencilBrowserRevisionProof["beforeMetrics"],
  right: OpenPencilBrowserRevisionProof["beforeMetrics"],
): boolean {
  return (
    left.pages === right.pages &&
    left.totalNodes === right.totalNodes &&
    left.variables === right.variables &&
    left.variableCollections === right.variableCollections &&
    left.images === right.images
  );
}

function assertBrowserProofAuthority(
  proof: OpenPencilBrowserRevisionProof,
  patch: OpenPencilFigEditPatch,
  outputFileName: string,
): void {
  if (
    proof.schemaVersion !== OPENPENCIL_BROWSER_REVISION_PROOF_SCHEMA ||
    proof.execution !== "web-worker" ||
    proof.requiresBrowserExtension !== false ||
    proof.patchId !== patch.patchId ||
    proof.baseSourceId !== patch.baseSourceId ||
    proof.baseSha256 !== patch.baseSha256 ||
    proof.outputFileName !== outputFileName ||
    proof.outputSha256 === patch.baseSha256 ||
    proof.operations.length !== patch.operations.length ||
    !proof.aggregateMetricsPreserved ||
    !proof.semanticReadbackVerified ||
    !proof.exportReopenVerified
  ) {
    throw new Error("Browser OpenPencil proof does not match its patch.");
  }
}

function exactRecord(
  left: Record<string, string | number | boolean | null>,
  right: Record<string, string | number | boolean | null>,
): boolean {
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  return (
    leftKeys.length === rightKeys.length &&
    leftKeys.every(
      (key, index) =>
        key === rightKeys[index] && Object.is(left[key], right[key]),
    )
  );
}

function assertCanonicalParity(
  proof: OpenPencilBrowserRevisionProof,
  evidence: OpenPencilFigRevisionEvidence,
  patch: OpenPencilFigEditPatch,
): void {
  if (
    proof.patchId !== evidence.patchId ||
    proof.baseSourceId !== evidence.baseSourceId ||
    proof.baseSha256 !== evidence.baseSha256 ||
    !sameMetrics(proof.beforeMetrics, evidence.beforeMetrics) ||
    !sameMetrics(proof.afterMetrics, evidence.afterMetrics) ||
    proof.operations.length !== evidence.operations.length
  ) {
    throw new Error(
      "Browser OpenPencil proof diverged from canonical revision evidence.",
    );
  }
  for (let index = 0; index < proof.operations.length; index += 1) {
    const browserOperation = proof.operations[index];
    const canonicalOperation = evidence.operations[index];
    const requestedOperation = patch.operations[index]!;
    if (requestedOperation.operation === "set-variable-mode-value") {
      if (
        browserOperation.operation !== "set-variable-mode-value" ||
        canonicalOperation.operation !== "set-variable-mode-value" ||
        browserOperation.index !== canonicalOperation.index ||
        browserOperation.variableId !== requestedOperation.variableId ||
        canonicalOperation.variableId !== requestedOperation.variableId ||
        browserOperation.collectionId !== requestedOperation.collectionId ||
        canonicalOperation.collectionId !== requestedOperation.collectionId ||
        browserOperation.modeId !== requestedOperation.modeId ||
        canonicalOperation.modeId !== requestedOperation.modeId ||
        browserOperation.variableType !==
          requestedOperation.expectedVariableType ||
        canonicalOperation.variableType !==
          requestedOperation.expectedVariableType ||
        browserOperation.variableName !== canonicalOperation.variableName ||
        browserOperation.collectionName !== canonicalOperation.collectionName ||
        browserOperation.modeName !== canonicalOperation.modeName ||
        browserOperation.variableKey !== canonicalOperation.variableKey ||
        !exactRecord(browserOperation.before, canonicalOperation.before) ||
        !exactRecord(browserOperation.after, canonicalOperation.after)
      ) {
        throw new Error(
          `Browser OpenPencil operation ${index} diverged from canonical evidence.`,
        );
      }
      continue;
    }
    if (
      browserOperation.operation === "set-variable-mode-value" ||
      canonicalOperation.operation === "set-variable-mode-value"
    ) {
      throw new Error(
        `Browser OpenPencil operation ${index} diverged from canonical evidence.`,
      );
    }
    const nodeIdentityMatches = requestedOperation.sourceNodeId
      ? browserOperation.sourceNodeId === requestedOperation.sourceNodeId &&
        canonicalOperation.sourceNodeId === requestedOperation.sourceNodeId
      : browserOperation.nodeId === canonicalOperation.nodeId;
    if (
      browserOperation.index !== canonicalOperation.index ||
      browserOperation.operation !== canonicalOperation.operation ||
      !nodeIdentityMatches ||
      browserOperation.nodeType !== canonicalOperation.nodeType ||
      !exactRecord(browserOperation.before, canonicalOperation.before) ||
      !exactRecord(browserOperation.after, canonicalOperation.after)
    ) {
      throw new Error(
        `Browser OpenPencil operation ${index} diverged from canonical evidence.`,
      );
    }
  }
}

export async function runOpenPencilBrowserRevisionWorker(input: {
  source: Blob;
  patch: OpenPencilFigEditPatch;
  outputFileName: string;
  signal?: AbortSignal;
  workerFactory?: () => BrowserRevisionWorker;
}): Promise<OpenPencilBrowserRevisionArtifact> {
  if (input.signal?.aborted) {
    throw input.signal.reason ?? new DOMException("Aborted", "AbortError");
  }
  const sourceBuffer = await input.source.arrayBuffer();
  const worker = (input.workerFactory ?? createBrowserRevisionWorker)();
  const request: OpenPencilBrowserRevisionWorkerRequest = {
    sourceBuffer,
    patch: input.patch,
    outputFileName: input.outputFileName,
  };

  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = () => {
      worker.terminate();
      input.signal?.removeEventListener("abort", abort);
    };
    const fail = (error: unknown) => {
      if (settled) return;
      settled = true;
      finish();
      reject(error);
    };
    const abort = () =>
      fail(input.signal?.reason ?? new DOMException("Aborted", "AbortError"));
    input.signal?.addEventListener("abort", abort, { once: true });
    worker.onerror = (event) => {
      fail(
        new Error(
          event.message || "Browser OpenPencil revision worker crashed.",
        ),
      );
    };
    worker.onmessage = (event) => {
      void (async () => {
        if (!event.data.ok) {
          if (event.data.reason) {
            throw new OpenPencilBrowserEngineUnavailableError(
              event.data.reason,
              event.data.error,
            );
          }
          throw new Error(event.data.error);
        }
        const { outputBuffer, proof } = event.data;
        assertBrowserProofAuthority(proof, input.patch, input.outputFileName);
        if (
          outputBuffer.byteLength !== proof.outputSizeBytes ||
          (await sha256Buffer(outputBuffer.slice(0))) !== proof.outputSha256
        ) {
          throw new Error(
            "Browser OpenPencil output bytes do not match worker proof.",
          );
        }
        if (settled) return;
        settled = true;
        finish();
        resolve({
          blob: new Blob([outputBuffer], {
            type: "application/octet-stream",
          }),
          fileName: proof.outputFileName,
          proof,
        });
      })().catch(fail);
    };
    worker.postMessage(request, [sourceBuffer]);
  });
}

const DEFAULT_DEPENDENCIES: BrowserFirstRevisionDependencies = {
  loadSourceFile: loadOpenPencilSourceFile,
  runBrowserRevision: runOpenPencilBrowserRevisionWorker,
  commitCanonicalRevision: requestOpenPencilFigRevision,
};

export async function requestOpenPencilBrowserFirstRevision(
  input: {
    bundle: OpenPencilFigmaSourceBundle;
    patch: OpenPencilFigEditPatch;
    signal?: AbortSignal;
    onStatus?: (status: string) => void;
    onBrowserArtifact?: (artifact: OpenPencilBrowserRevisionArtifact) => void;
    beforeCanonicalCommit?: () => void | Promise<void>;
  },
  dependencies: BrowserFirstRevisionDependencies = DEFAULT_DEPENDENCIES,
): Promise<OpenPencilBrowserFirstRevisionResult> {
  input.onStatus?.("Loading retained .fig bytes for browser proof");
  const source = await dependencies.loadSourceFile(input.bundle, input.signal);
  input.onStatus?.("Editing and reopening .fig in a browser worker");
  let browserArtifact: OpenPencilBrowserRevisionArtifact | null = null;
  let browserProof: OpenPencilBrowserProofOutcome;
  try {
    browserArtifact = await dependencies.runBrowserRevision({
      source,
      patch: input.patch,
      outputFileName: browserRevisionFileName(input.bundle),
      signal: input.signal,
    });
    browserProof = { status: "verified" };
    input.onBrowserArtifact?.(browserArtifact);
  } catch (error) {
    // ONLY an unrunnable engine degrades. Anything else -- a bad patch, a
    // mismatched proof, an aborted signal -- is a real failure of this
    // revision and still stops the flow, because degrading it would turn a
    // defect into a silently server-only commit.
    if (!(error instanceof OpenPencilBrowserEngineUnavailableError)) throw error;
    browserProof = {
      status: "unavailable",
      reason: error.reason,
      detail: error.message,
    };
    input.onStatus?.(
      "Browser .fig engine unavailable here; committing through the source revision service",
    );
  }
  await input.beforeCanonicalCommit?.();
  if (browserArtifact) {
    input.onStatus?.("Browser export/reopen verified; committing revision");
  }
  const canonical = await dependencies.commitCanonicalRevision({
    bundle: input.bundle,
    patch: input.patch,
    signal: input.signal,
    onStatus: input.onStatus,
  });
  // Parity can only be asserted against a proof that exists. When it does not,
  // `browserProof.status` carries that fact to the caller instead.
  if (browserArtifact) {
    assertCanonicalParity(
      browserArtifact.proof,
      canonical.evidence,
      input.patch,
    );
  }
  return { ...canonical, browserArtifact, browserProof };
}

export function downloadOpenPencilBrowserRevision(
  artifact: OpenPencilBrowserRevisionArtifact,
): void {
  const objectUrl = URL.createObjectURL(artifact.blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = artifact.fileName;
    anchor.hidden = true;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
  }
}
